namespace Solovis.Reports.Parameters {

  export const SourceDataHelpText: string = "When selected, book values are used.";
  export const BlendedValuesHelpText: string = "<p>If checked, the environment configured cutoff dates will be used and booked values will be used"
    + " up to and including the cutoff date, and live values will be used after the cutoff date.</p>"
    + "<p>NOTE: You will almost always want to use 'Recompute If Possible' for 'Stored Performance' when using this option.</p>"
    + "<p>NOTE: Blended values will not currently be used for Attribution Benchmark Source data.</p>";

  export class SourceDataParameterControl extends Solovis.UI.BaseControl {

    private _sourceData;

    private _sourceDataLabel;
    private _sourceDataEditLink;

    private _settings;
    private _parameterSet;
    public static hasNewRender = true;

    constructor(options) {
      super(options);

      this._sourceData = (typeof options.initialValue != "undefined") ? options.initialValue : null;

      this._settings = (options && options.settings) ? options.settings : {};
      this._parameterSet = options.parameterList.parameterSet;
    }

    public doRender() {
      if (this.options.renderMode === 'modern') {
        this.doRenderNew();
      } else {
        this.doRenderClassic();
      }
    }

    private async doRenderNew() {
      await Solovis.UX.Utils.placeComponent("ConfigureControl", this.container[0], { onClick: () => { this._showSourceDataEditor() } })
    }

    private doRenderClassic() {
      let self = this;

      this._sourceDataLabel = $("<a>").text("Source Data").appendTo(this.container);
      this._sourceDataLabel.click(function () {
        self._showSourceDataEditor();
      });

      this._sourceDataEditLink = $("<a>").appendTo(this.container)
        .css("padding-left", "5px")
        .html("<span class='fa fa-pencil'></span>");
      this._sourceDataEditLink.click(function () {
        self._showSourceDataEditor();
      });
    }


    private _showSourceDataEditor() {
      let editorDialog = new SourceDataEditorDialog({
        settings: this._settings,
        parameterSet: this._parameterSet,
        saveCallback: (newSourceData) => {
          this.setParameterValue(newSourceData);
        }
      });

      editorDialog.show();
    }


    public _setCurrentSourceDataOptions(newSourceData) {
      this._sourceData = newSourceData;
    }

    public setParameterValue(newValue, more?) {
      Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
        this, SourceDataParameterControl, this._setCurrentSourceDataOptions, newValue, more);
    }

    public getParameterValue() {
      return Solovis.deepCopy(this._sourceData);
    }

    public notifyParameterValueChanged() {
      $(this).trigger("parameterValueChanged");
    }
  }


  export class SourceDataEditorDialog {

    private _sourceData;
    private _saveCallback;

    private _formBuilder: Solovis.UI.InputControls.FormBuilder;
    private _dialog: Solovis.UI.Dialog;

    private _settings;
    private _assetMode

    public constructor(options) {
      this._sourceData = options.parameterSet._paramNameToHolderMap.sourceData ? options.parameterSet._paramNameToHolderMap.sourceData.value : {};
      this._saveCallback = options.saveCallback;

      this._assetMode = options.parameterSet._paramNameToHolderMap.assetMode
        ? options.parameterSet._paramNameToHolderMap.assetMode.value
        : null;

      this._settings = (options && options.settings) ? options.settings : {};
    }

    public show() {
      let sourceData = this._sourceData ? this._sourceData : {};

      let formBuilderDiv = $("<div>");
      let formBuilder = new Solovis.UI.InputControls.FormBuilder({
        container: formBuilderDiv,
        width: 700,
        labelColumnWidth: 270
      });
      this._formBuilder = formBuilder;

      formBuilder.addEntry({
        name: "storedPerformance",
        label: "Stored Performance",
        help: {
          html: "<p>When 'Use Stored Performance If Possible' is selected the month-to-date performance that is saved in the Solovis database"
            + " for a given entity or investment on a given day will be used. This number will not be updated to reflect new data until the "
            + "Value Estimate Updater is rerun. Note that this will have no impact on composite returns, which will always be recomputed based on all available data.</p>"
            + "<p>When 'Recompute If Possible' is selected the report will recalculate all returns based on available underlying data.</p>"
        },

        initialValue: sourceData.storedPerformance,

        editor: {
          type: "select",
          selectOptions: {
            data: [
              { text: "Use Stored Performance If Possible", value: "UseStoredPerformanceIfPossible" },
              { text: "Recompute If Possible", value: "RecomputeIfPossible" }
            ]
          }
        },

        isVisible: () => !this._settings.hideStoredPerformance
      });

      formBuilder.addEntry({
        name: "useBookedValues",
        label: "Use Booked Values",
        help: Solovis.Reports.Parameters.SourceDataHelpText,
        initialValue: !!sourceData.useBookedValues,
        editor: {
          type: "bool"
        }
      });

      formBuilder.addEntry({
        name: "useBlendedValues",
        label: "Use Blended Values",
        help: { html: Solovis.Reports.Parameters.BlendedValuesHelpText },
        initialValue: !!sourceData.useBlendedValues,

        editor: {
          type: "bool"
        },

        isVisible: (formEntry) => {
          if (this._settings.hideUseBlendedValues) {
            return false;
          }

          // Only show if useBookedValues is checked.
          return formEntry.formBuilder.getValueByName("useBookedValues");
        }
      });

      formBuilder.addEntry({
        name: "compositesUseChildrensTwrDates",
        label: "Composites Use Children's TWR Dates",
        help: {
          html: "When selected, composite lines will inherit the TWR from and to dates from the child rows that make up that composite."
            + " For instance, an investment's inception date will apply to any composite of which it is a component"
            + " (assuming that that investment's inception date is the earliest in the composite)."
        },

        initialValue: !!sourceData.compositesUseChildrensTwrDates,

        editor: {
          type: "bool"
        },

        isVisible: (formEntry) => {
          return this._assetMode == "Composite";
        }
      });

      formBuilder.addEntry({
        name: "knowledgeDateTimestamp",
        label: "Knowledge Date",
        help: {
          html: "This date will be used when pulling market values and stored performance for investment line items. "
            + "Transaction updated at dates are <u>not</u> queried. All composite lines items are calculated based on underlying line items."
        },

        initialValue: sourceData.knowledgeDateTimestamp,

        editor: {
          type: "utcTimestamp",
          utcTimestampOptions: {
            displayMode: "local"
          }
        }
      });

      formBuilder.addEntry({
        name: "attributionBenchmarkSource",
        label: "Attribution Benchmark Source",

        help: {
          html: "<p>'Attribution Benchmark Source' indicates where returns for the policy benchmarks should be loaded from.</p>"
            + "<p>Historical Context: The original design of attribution dictated that benchmark returns for policy benchmarks"
            + " were always loaded from the 'Live' tables, so that attribution analysis could be examined for booked portfolio holding"
            + " values without having to book all of the benchmarks. We now provide an option for benchmark returns for policy benchmarks"
            + " to be loaded from either Live or Booked tables (or same as portfolio source data). The default remains 'Live' to"
            + " preserve past behavior with pre-existing dashboards. In the future, we may provide an option to change the default"
            + " behavior for a given data environment.</p>"
        },

        initialValue: sourceData.attributionBenchmarkSource ? sourceData.attributionBenchmarkSource : "Default",

        editor: {
          type: "select",
          selectOptions: {
            data: [
              { text: "Default (Live)", value: "Default" },
              { text: "Live Values", value: "Live" },
              { text: "Booked Values", value: "Book" },
              { text: "Same", value: "Same" }
            ]
          }
        },

        isVisible: () => !this._settings.hideAttributionBenchmarkSource
      });

      formBuilder.setupDone();

      formBuilder.showAsDialog({
        title: "Edit Source Data",

        desiredWidth: 720,
        desiredHeight: 300,

        showSaveCancel: true,
        saveButtonText: "OK",

        saveCallback: () => {
          let newSourceData = this._getSourceData();

          this._saveCallback(newSourceData);

          return true;
        }
      });
    }

    private _getSourceData() {
      let newSourceData: any = {};

      newSourceData.storedPerformance = this._formBuilder.getValueByName("storedPerformance");

      if (this._formBuilder.getValueByName("useBookedValues")) {
        newSourceData.useBookedValues = true;

        if (this._formBuilder.getValueByName("useBlendedValues")) {
          newSourceData.useBlendedValues = true;
        }
      }

      if (this._formBuilder.getValueByName("compositesUseChildrensTwrDates")) {
        newSourceData.compositesUseChildrensTwrDates = true;
      }

      let knowledgeDateTimestamp = this._formBuilder.getValueByName("knowledgeDateTimestamp");
      if (knowledgeDateTimestamp) {
        newSourceData.knowledgeDateTimestamp = knowledgeDateTimestamp;
      }

      let attributionBenchmarkSource = this._formBuilder.getValueByName("attributionBenchmarkSource");
      if (attributionBenchmarkSource && attributionBenchmarkSource != 'Default') {
        newSourceData.attributionBenchmarkSource = attributionBenchmarkSource;
      }

      return newSourceData;
    }
  }
}